/**
 * FSPIOP Flow Simulator — scenario definitions.
 *
 * Actors: 0 = Payer DFSP (you)   1 = Mojaloop Hub   2 = Payee DFSP
 *
 * Unlike the ISO simulator there is no live generate endpoint for FSPIOP wire
 * bodies, so each step carries an illustrative `body` (from the integration
 * guide) and `applies` tags that link to the relevant Build & test guide —
 * showing where JWS signing, async correlation, and ILP verification each occur
 * within the party lookup → quote → transfer choreography.
 */

export const FSPIOP_ACTORS = ['Payer DFSP', 'Mojaloop Hub', 'Payee DFSP'];

// applies tags → guide routes
export const GUIDE_LINKS = {
  jws:   { label: 'JWS signed',        href: '/guides/signing' },
  async: { label: 'Async callback',    href: '/guides/async-callbacks' },
  ilp:   { label: 'ILP condition',     href: '/guides/ilp' },
};

export const FSPIOP_SCENARIOS = {
  'party-lookup': {
    id: 'party-lookup',
    label: 'Party lookup',
    description: 'Resolve which FSP owns an account before quoting.',
    steps: [
      {
        from: 0, to: 1, msgType: 'GET /parties', kind: 'req', applies: ['jws'],
        title: 'Look up the payee',
        description: "The payer DFSP asks the hub who owns an MSISDN. The request is JWS-signed; there is no FSPIOP-Destination yet (the owner is unknown).",
        body: 'GET /parties/MSISDN/250788123456\nFSPIOP-Source: yourbank\nFSPIOP-Signature: {"signature":"…","protectedHeader":"…"}',
      },
      {
        from: 1, to: 2, msgType: 'GET /parties', kind: 'req', applies: [],
        title: 'Hub routes the lookup',
        description: 'The hub forwards the lookup to every FSP (or the one it believes owns the identifier).',
      },
      {
        from: 2, to: 1, msgType: 'PUT /parties', kind: 'ok', applies: ['async'],
        title: 'Owner responds',
        description: "The owning FSP calls back with the party details. This is the async callback the connector correlates by the resource key (MSISDN:250788123456).",
        body: '{\n  "party": {\n    "partyIdInfo": { "partyIdType": "MSISDN", "partyIdentifier": "250788123456", "fspId": "bankofkigali" },\n    "name": "Jean Damascene"\n  }\n}',
      },
      {
        from: 1, to: 0, msgType: 'PUT /parties', kind: 'ok', applies: ['async'],
        title: 'Resolved to the payer',
        description: 'The hub relays the resolution. The connector completes the parked operation and returns fspId + name to the CBS.',
      },
    ],
  },

  'quote': {
    id: 'quote',
    label: 'Quote',
    description: 'Agree the transfer amount, fees, and obtain the ILP condition.',
    steps: [
      {
        from: 0, to: 1, msgType: 'POST /quotes', kind: 'req', applies: ['jws'],
        title: 'Request a quote',
        description: 'The payer DFSP requests terms for the transfer. JWS-signed, now with FSPIOP-Destination = the payee FSP resolved in the lookup.',
        body: '{\n  "quoteId": "3d492671-…",\n  "transactionId": "…",\n  "payer": { "partyIdInfo": { "partyIdType": "MSISDN", "partyIdentifier": "250788000001", "fspId": "yourbank" } },\n  "payee": { "partyIdInfo": { "partyIdType": "MSISDN", "partyIdentifier": "250788123456", "fspId": "bankofkigali" } },\n  "amountType": "SEND",\n  "amount": { "amount": "10000.00", "currency": "RWF" }\n}',
      },
      {
        from: 1, to: 2, msgType: 'POST /quotes', kind: 'req', applies: [],
        title: 'Hub routes the quote',
        description: 'The hub forwards the quote request to the payee FSP.',
      },
      {
        from: 2, to: 1, msgType: 'PUT /quotes', kind: 'ok', applies: ['async', 'ilp'],
        title: 'Payee returns terms + ILP',
        description: "The payee FSP prices the transfer and mints the ilpPacket and condition. These are the cryptographic binding for the transfer step — shuttled, never regenerated.",
        body: '{\n  "transferAmount": { "amount": "10000.00", "currency": "RWF" },\n  "payeeReceiveAmount": { "amount": "9950.00", "currency": "RWF" },\n  "payeeFspFee": { "amount": "50.00", "currency": "RWF" },\n  "ilpPacket": "AYIBgQAAAAAAAASwNgsBk…",\n  "condition": "HOr22-H3rudl6cg…",\n  "expiration": "2026-02-27T10:30:00.000Z"\n}',
      },
      {
        from: 1, to: 0, msgType: 'PUT /quotes', kind: 'ok', applies: ['async', 'ilp'],
        title: 'Terms relayed to payer',
        description: 'The connector stores the ilpPacket and condition against the quote, ready to pass verbatim into the transfer.',
      },
    ],
  },

  'transfer': {
    id: 'transfer',
    label: 'Transfer',
    description: 'Move the funds atomically, bound by the ILP condition/fulfilment.',
    steps: [
      {
        from: 0, to: 1, msgType: 'POST /transfers', kind: 'req', applies: ['jws', 'ilp'],
        title: 'Initiate the transfer',
        description: 'The payer DFSP sends the transfer carrying the ilpPacket and condition from the quote, verbatim. JWS-signed.',
        body: '{\n  "transferId": "f1e2d3c4-…",\n  "payerFsp": "yourbank",\n  "payeeFsp": "bankofkigali",\n  "amount": { "amount": "10000.00", "currency": "RWF" },\n  "ilpPacket": "AYIBgQAAAAAAAASwNgsBk…",\n  "condition": "HOr22-H3rudl6cg…",\n  "expiration": "2026-02-27T10:30:00.000Z"\n}',
      },
      {
        from: 1, to: 2, msgType: 'POST /transfers', kind: 'req', applies: [],
        title: 'Hub routes the transfer',
        description: 'The hub reserves funds and forwards the transfer to the payee FSP.',
      },
      {
        from: 2, to: 1, msgType: 'PUT /transfers', kind: 'ok', applies: ['async', 'ilp'],
        title: 'Payee fulfils (credits)',
        description: "The payee FSP credits its customer and returns the fulfilment — the SHA-256 preimage of the condition. SHA-256(fulfilment) must equal the condition.",
        body: '{\n  "transferState": "COMMITTED",\n  "fulfilment": "mhPut9r7…",\n  "completedTimestamp": "2026-02-27T10:28:15.234Z"\n}',
      },
      {
        from: 1, to: 0, msgType: 'PUT /transfers', kind: 'ok', applies: ['async', 'ilp'],
        title: 'Fulfilment relayed',
        description: 'The hub relays the fulfilment. The connector verifies SHA-256(fulfilment) == condition before treating the transfer as settled.',
      },
      {
        from: 1, to: 2, msgType: 'PATCH /transfers', kind: 'ok', applies: ['async'],
        title: 'Commit notification to the payee (v1.1, optional)',
        description: 'Sent only when the payee answered with transferState RESERVED instead of COMMITTED: the hub tells the payee FSP the transfer is now committed, so it can finalise its books. The payer receives no PATCH; its transfer ends with the PUT above.',
      },
    ],
  },
};

export const FSPIOP_SCENARIO_ORDER = ['party-lookup', 'quote', 'transfer'];
